import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { Calendar as CalIcon, ListFilter, ArrowDown, LayoutGrid, List } from "lucide-react";
import Navbar from "@/components/Navbar";
import EventCard from "@/components/EventCard";
import IcsBuilder from "@/components/IcsBuilder";
import CalendarMonth from "@/components/CalendarMonth";
import MultiSelect from "@/components/MultiSelect";
import Seo from "@/components/Seo";
import { SITE_URL, SITE_NAME } from "@/constants/site";
import { SponsorTile, SponsorFooter } from "@/components/Sponsors";
import { useCategories } from "@/context/CategoriesContext";
import { SPORTS, TEAMS } from "@/constants/categories";
import { DEFAULT_CITY } from "@/constants/cities";
import { useCities } from "@/context/CitiesContext";
import { api } from "@/lib/api";

export default function Home() {
  const { city: citySlug } = useParams();
  const navigate = useNavigate();
  const { getCity, loading: citiesLoading } = useCities();
  const city = getCity(citySlug);
  const { sports: allCats, teams: allOrgs, sportLabel } = useCategories();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sports, setSports] = useState(SPORTS.map((s) => s.id));
  const [teams, setTeams] = useState(TEAMS.map((t) => t.id));
  const [layout, setLayout] = useState("list"); // list | calendar
  const [range, setRange] = useState("week"); // week | fortnight | month | days31

  const load = () => {
    setLoading(true);
    if (sports.length === 0 || teams.length === 0) {
      setEvents([]); setLoading(false); return;
    }
    const params = { city: citySlug };
    if (sports.length < allCats.length) params.sports = sports.join(",");
    if (teams.length < allOrgs.length) params.teams = teams.join(",");
    api
      .get("/events", { params })
      .then(({ data }) => setEvents(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (city) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sports, teams, citySlug, city]);


  const rangeDays = { week: 7, fortnight: 14, days31: 31 }[range];

  // Events filtered to the selected time range (used by the list view).
  const windowed = useMemo(() => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    let end;
    if (range === "month") {
      end = new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59, 999);
    } else {
      end = new Date(start); end.setDate(end.getDate() + rangeDays); end.setHours(23, 59, 59, 999);
    }
    return events.filter((e) => {
      const d = new Date(e.start_at);
      return d >= start && d <= end;
    });
  }, [events, range, rangeDays]);

  // Calendar grid: for "month" show all events (so month nav works); for day-ranges show only the windowed set.
  const calendarEvents = range === "month" ? events : windowed;

  const rangeLabel = range === "month" ? "this month" : `the next ${rangeDays} days`;

  const grouped = useMemo(() => {
    const map = {};
    windowed.forEach((e) => {
      const key = new Date(e.start_at).toLocaleDateString("en-GB", {
        weekday: "long", day: "numeric", month: "long",
      });
      (map[key] = map[key] || []).push(e);
    });
    return map;
  }, [windowed]);

  if (!city) {
    if (citiesLoading) {
      return (
        <div className="App">
          <Navbar />
          <p className="text-center text-zinc-500 overline py-24">Loading…</p>
        </div>
      );
    }
    return <Navigate to={`/${DEFAULT_CITY}`} replace />;
  }

  const seoTitle = `${city.name} Events Calendar — Sport & Theatre | ${SITE_NAME}`;
  const seoDesc = `Every rugby, football, racing, cricket, basketball and live theatre event in ${city.name}. One calendar — filter by team or venue and subscribe to a tailored feed.`;
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${city.name} events`,
      itemListElement: windowed.slice(0, 30).map((e, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}/${city.slug}/event/${e.id}`,
        name: e.title,
      })),
    },
  ];

  return (
    <div className="App">
      <Seo title={seoTitle} description={seoDesc} path={`/${city.slug}`} jsonLd={jsonLd} />
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <img src={city.hero} alt={`${city.name} sport`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-ec-bg via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <p className="overline text-ec-volt mb-4 animate-fade-up">{city.tagline}</p>
          <h1 className="font-heading text-5xl sm:text-7xl md:text-8xl leading-[0.85] tracking-tight max-w-4xl animate-fade-up">
            EVERY EVENT.<br />
            EVERY VENUE.<br />
            <span className="text-ec-volt">ONE CALENDAR.</span>
          </h1>
          <p className="text-zinc-300 mt-6 max-w-xl text-base sm:text-lg animate-fade-up" style={{ animationDelay: "100ms" }}>
            Rugby, football, racing, cricket, basketball and live theatre across {city.name} —
            consolidated and subscribable. Never miss an event again.
          </p>
          <div className="flex flex-wrap gap-3 mt-8 animate-fade-up" style={{ animationDelay: "150ms" }}>
            <a href="#calendar" data-testid="hero-browse-btn" className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-6 py-3.5 hover:-translate-y-0.5 hover:shadow-[0_0_18px_rgba(204,255,0,0.4)] transition-all">
              <CalIcon size={16} /> Browse Events
            </a>
            <a href="#subscribe" data-testid="hero-subscribe-btn" className="inline-flex items-center gap-2 overline border border-white/25 px-6 py-3.5 hover:border-ec-volt hover:text-ec-volt transition-colors">
              <ArrowDown size={16} /> Subscribe to Feed
            </a>
          </div>
        </div>
      </section>

      {/* FILTERS + CALENDAR */}
      <section id="calendar" className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2.5">
            <ListFilter className="text-ec-volt" size={20} />
            <h2 className="font-heading text-4xl tracking-wide leading-none">Events</h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Layout toggle: List | Calendar */}
            <div className="flex border border-white/15" data-testid="layout-toggle">
              {[
                { id: "list", label: "List", icon: List },
                { id: "calendar", label: "Calendar", icon: LayoutGrid },
              ].map((l, i) => (
                <button
                  key={l.id}
                  data-testid={`layout-${l.id}`}
                  onClick={() => setLayout(l.id)}
                  className={`inline-flex items-center gap-2 overline px-3.5 sm:px-4 py-2.5 transition-colors ${i > 0 ? "border-l border-white/15" : ""} ${layout === l.id ? "bg-ec-volt text-black" : "text-zinc-300 hover:text-white"}`}
                >
                  <l.icon size={15} /> {l.label}
                </button>
              ))}
            </div>
            {/* Range selector */}
            <div className="flex flex-wrap border border-white/15" data-testid="range-toggle">
              {[
                { id: "week", label: "7 Days" },
                { id: "fortnight", label: "14 Days" },
                { id: "month", label: "Month" },
                { id: "days31", label: "31 Days" },
              ].map((r, i) => (
                <button
                  key={r.id}
                  data-testid={`range-${r.id}`}
                  onClick={() => setRange(r.id)}
                  className={`overline px-3.5 sm:px-4 py-2.5 transition-colors ${i > 0 ? "border-l border-white/15" : ""} ${range === r.id ? "bg-ec-volt text-black" : "text-zinc-300 hover:text-white"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-10" data-testid="filters">
          <MultiSelect
            label="Categories"
            testid="filter-categories"
            options={allCats}
            selected={sports}
            onChange={setSports}
          />
          <MultiSelect
            label="Organisers"
            testid="filter-organisers"
            options={allOrgs.map((t) => ({ ...t, group: t.sport, groupLabel: sportLabel(t.sport) }))}
            selected={teams}
            onChange={setTeams}
          />
          {(sports.length < allCats.length || teams.length < allOrgs.length) && (
            <button
              data-testid="filter-reset"
              onClick={() => { setSports(allCats.map((s) => s.id)); setTeams(allOrgs.map((t) => t.id)); }}
              className="overline text-zinc-400 hover:text-ec-volt transition-colors px-2 py-2.5"
            >
              Reset filters
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-zinc-500 overline">Loading events…</p>
        ) : layout === "calendar" ? (
          <CalendarMonth
            events={calendarEvents}
            days={range === "month" ? undefined : rangeDays}
            onEventClick={(id) => navigate(`/${city.slug}/event/${id}`)}
          />
        ) : windowed.length === 0 ? (
          <div className="border border-white/10 bg-ec-surface p-12 text-center">
            <p className="font-heading text-3xl text-zinc-400">Nothing on for {rangeLabel}</p>
            <p className="text-zinc-500 mt-2">Try a longer range, the Calendar view, or a different filter.</p>
          </div>
        ) : (
          <div className="space-y-12" data-testid="events-list">
            <p className="overline text-zinc-500">Showing {rangeLabel} · {windowed.length} event{windowed.length > 1 ? "s" : ""}</p>
            {Object.entries(grouped).map(([date, items]) => (
              <div key={date}>
                <div className="flex items-center gap-4 mb-5">
                  <h3 className="overline text-ec-volt whitespace-nowrap">{date}</h3>
                  <div className="h-px bg-white/10 flex-1" />
                  <span className="overline text-zinc-600">{items.length} event{items.length > 1 ? "s" : ""}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {items.map((e, i) => (
                    <EventCard key={e.id} event={e} index={i} city={city.slug} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* SUBSCRIBE */}
      <section id="subscribe" className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2">
            <IcsBuilder city={city.slug} />
          </div>
          <SponsorTile city={city.slug} />
        </div>
      </section>

      <SponsorFooter city={city.slug} />

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-heading text-2xl tracking-wide">EVENT CITY<span className="text-ec-volt">.</span> {city.name.toUpperCase()}</p>
          <div className="flex gap-6 overline text-zinc-500">
            <Link to={`/${city.slug}/submit`} className="hover:text-ec-volt transition-colors">Submit an Event</Link>
            <Link to="/admin" className="hover:text-ec-volt transition-colors">Admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
